import React, { createContext, useContext, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";

const AlertContext = createContext(null);

export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showAlert = (message, severity = "success") => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  const showSuccess = (message) => {
    showAlert(message, "success");
  };

  const showError = (message) => {
    showAlert(message, "error");
  };

  const showWarning = (message) => {
    showAlert(message, "warning");
  };

  const showInfo = (message) => {
    showAlert(message, "info");
  };

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;

    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const value = useMemo(
    () => ({
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    []
  );

  return (
    <AlertContext.Provider value={value}>
      {children}

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          mt: 1,
          mr: 1,
        }}
      >
        <Alert
          onClose={handleClose}
          severity={alert.severity}
          variant="standard"
          sx={{
            minWidth: { xs: 280, sm: 340 },
            borderRadius: "14px",
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "0 18px 45px rgba(17,24,39,0.16)",
            alignItems: "center",
            border: "1px solid",
            "& .MuiAlert-icon": {
              fontSize: 22,
            },
            ...(alert.severity === "success" && {
              bgcolor: "#EDF7ED",
              color: "#1E4620",
              borderColor: "#C8E6C9",
              "& .MuiAlert-icon": {
                color: "#2E7D32",
              },
              "& .MuiAlert-action": {
                color: "#1E4620",
              },
            }),
            ...(alert.severity === "error" && {
              bgcolor: "#FDEDED",
              color: "#5F2120",
              borderColor: "#F5C2C7",
              "& .MuiAlert-icon": {
                color: "#D32F2F",
              },
              "& .MuiAlert-action": {
                color: "#5F2120",
              },
            }),
            ...(alert.severity === "warning" && {
              bgcolor: "#FFF4E5",
              color: "#663C00",
              borderColor: "#FFE0B2",
              "& .MuiAlert-icon": {
                color: "#ED6C02",
              },
              "& .MuiAlert-action": {
                color: "#663C00",
              },
            }),
            ...(alert.severity === "info" && {
              bgcolor: "#E5F6FD",
              color: "#014361",
              borderColor: "#B3E5FC",
              "& .MuiAlert-icon": {
                color: "#0288D1",
              },
              "& .MuiAlert-action": {
                color: "#014361",
              },
            }),
          }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = () => useContext(AlertContext);